import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useClientScope } from '@/contexts/ClientScopeContext';
import { useRegisteredPageTitle } from '@/lib/pageTitle';
import { ADMIN_SECTIONS, MODULE_MENUS, activeLinkFor, isScopeAware, moduleForPath } from '@/lib/navigation';
import { MODULES } from '@/lib/modules';
import { ROUTES } from '@/lib/routes';

/** A page's name from the menus when it hasn't registered one (lib/pageTitle.ts). */
function fallbackTitle(pathname: string): string | null {
  if (pathname === ROUTES.home) return 'Home';
  if (pathname.startsWith(ROUTES.admin.root)) {
    for (const s of ADMIN_SECTIONS) for (const i of s.items) {
      if (pathname === i.to) return i.label === 'Settings' ? `${s.title.split(' · ')[0]} settings` : i.label;
    }
    return 'Settings';
  }
  if (pathname.startsWith(ROUTES.account.root)) return 'Settings';
  const mod = moduleForPath(pathname);
  if (!mod) return null;
  const link = activeLinkFor(pathname, MODULE_MENUS[mod] ?? []);
  return link?.label ?? MODULES.find((m) => m.id === mod)?.label ?? null;
}

/** Installed as an app (PWA): the browser already prefixes the window title with "Cosmos - ". */
const APP_QUERY = '(display-mode: standalone), (display-mode: window-controls-overlay), (display-mode: minimal-ui)';
function useInstalledApp() {
  return useSyncExternalStore(
    (cb) => { const m = window.matchMedia(APP_QUERY); m.addEventListener('change', cb); return () => m.removeEventListener('change', cb); },
    () => window.matchMedia(APP_QUERY).matches,
  );
}

/**
 * Writes the browser tab title — "<page> · <client> · Cosmos" — so each Cosmos window/tab is
 * recognisable on hover. The client (header picker) is added on pages that filter by it. The
 * " · Cosmos" suffix is dropped in the installed app, whose title bar already names it.
 */
export function DocumentTitle() {
  const { pathname } = useLocation();
  const { scope } = useClientScope();
  const registered = useRegisteredPageTitle();
  const installed = useInstalledApp();
  const page = registered ?? fallbackTitle(pathname);
  const client = scope && isScopeAware(pathname) ? scope.name : null;
  const title = [page, client].filter(Boolean).join(' · ') || 'Cosmos';
  const full = installed || title === 'Cosmos' ? title : `${title} · Cosmos`;
  useEffect(() => { document.title = full; }, [full]);
  return null;
}
